import { cn } from '@heroui/react'
import { useLogout } from '@qse/auth-ui'
import { useUserStore } from '@qse/stores'
import { useIsMobile } from '@qse/utils'
import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { PaymentOverdueBanner } from '../components/PaymentOverdueBanner'
import ProgressTracker from '../components/progress-tracker'
import { ProfileDropdown } from '../dropdown/profileDropdown'
import { useSearchFilterVisibility } from '../hooks/useSearchFilterVisibility'
import SearchFilter from './index'

const Navbar: React.FC<{ isTenantAdmin?: boolean }> = ({ isTenantAdmin = false }) => {
  const { user } = useUserStore()
  const isMobile = useIsMobile()
  const location = useLocation()
  const isSearchFilterVisible = useSearchFilterVisibility(
    (state: { isVisible: boolean }) => state.isVisible,
  )

  console.log('🚀 ~ Navbar ~ isSearchFilterVisible:', isSearchFilterVisible)
  const isSearchResults =
    location.pathname.includes('/dashboard/search-results') ||
    location.pathname.includes('/dashboard/favorites') ||
    location.pathname.includes('/dashboard/trash') ||
    location.pathname.startsWith('/folders/')
  const isOverview = location.pathname === '/dashboard'

  // For search results pages, always show search filter regardless of visibility state
  // For overview page, show search filter only when the main search filter is not visible (user scrolled down)
  const rawShouldShow = isSearchResults || (isOverview && !isSearchFilterVisible)

  // Stabilize the visibility on mobile to prevent flickering during scroll
  const [shouldShowSearchFilter, setShouldShowSearchFilter] = useState(rawShouldShow)
  const lastChangeTimeRef = useRef<number>(Date.now())
  const timeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    // For non-overview pages, update immediately
    if (!isOverview) {
      setShouldShowSearchFilter(rawShouldShow)

      return
    }

    // For overview page on mobile, add hysteresis to prevent rapid toggling
    if (isMobile) {
      const now = Date.now()
      const timeSinceLastChange = now - lastChangeTimeRef.current
      const minStableTime = 200 // Minimum time before allowing another state change

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // When showing the navbar search (main search is hidden), add minimal delay
      // to ensure smooth transition without overlap
      const transitionDelay = rawShouldShow ? 150 : 0 // 150ms for smooth handoff

      if (timeSinceLastChange >= minStableTime) {
        // Enough time has passed
        if (transitionDelay > 0) {
          timeoutRef.current = setTimeout(() => {
            setShouldShowSearchFilter(rawShouldShow)
            lastChangeTimeRef.current = Date.now()
          }, transitionDelay)
        } else {
          setShouldShowSearchFilter(rawShouldShow)
          lastChangeTimeRef.current = now
        }
      } else {
        // Too soon, delay the change
        const delay = minStableTime - timeSinceLastChange + transitionDelay

        timeoutRef.current = setTimeout(() => {
          setShouldShowSearchFilter(rawShouldShow)
          lastChangeTimeRef.current = Date.now()
        }, delay)
      }

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
      }
    } else {
      // Desktop: update immediately
      setShouldShowSearchFilter(rawShouldShow)
    }
  }, [rawShouldShow, isMobile, isOverview])

  // Only redirect to search-results from main dashboard, not from nested folders/trash/favorites
  const shouldRedirectToSearchResults = isOverview

  const { handleLogout } = useLogout()

  if (!user) {
    return null
  }

  return (
    <nav
      className={cn(
        'flex flex-col gap-4 md:px-[30px] px-4 pt-4 sticky top-0 w-full right-0 z-50 transition-colors duration-200 mb-5  min-h-20',
        shouldShowSearchFilter ? '' : '',
        location.pathname.includes('/dashboard/generate-keys') ||
          location.pathname.includes('/dashboard/generate-entropy')
          ? 'md:h-24 h-20'
          : '',
        location.pathname.includes('/profile')
          ? isMobile
            ? 'hidden'
            : ''
          : 'bg-gray-50 dark:bg-[#000]',
        isSearchResults ? 'justify-between' : '',
      )}
    >
      <ProgressTracker />

      {/* First space: Search box takes all available space */}

      {(location.pathname.includes('/dashboard/generate-keys') &&
        location.pathname.includes('/dashboard/generate-entropy')) ||
      location.pathname.includes('/profile') ? null : (
        <div
          className={cn(
            'lg:w-2/4 w-full overflow-hidden pb-1',
            shouldShowSearchFilter
              ? 'opacity-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 -translate-y-4 pointer-events-none absolute',
          )}
          style={{
            maxHeight: shouldShowSearchFilter ? '200px' : '0px',
            transition:
              'opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: isMobile ? 'opacity, transform' : 'auto',
          }}
        >
          <SearchFilter navbar redirectToSearchResults={shouldRedirectToSearchResults} />
        </div>
      )}
      {isTenantAdmin &&
        !location.pathname.includes('/profile') &&
        !location.pathname.includes('/dashboard/generate-keys') &&
        !location.pathname.includes('/dashboard/generate-entropy') && (
          <PaymentOverdueBanner isInView={isSearchFilterVisible} />
        )}

      {/* Second space: Empty space with ProfileDropdown at the end */}
      <div className="flex-1 lg:flex justify-end z-50 fixed right-4 top-4 hidden">
        <ProfileDropdown user={user} onLogout={handleLogout} />
      </div>
    </nav>
  )
}

export default Navbar
